import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { facsimileUrl, loadBuildInfo, loadFacsimile } from '../../data/load';
import type { FacsimilePage } from '../../data/types';

const IA_PAGE = (page: number) => `https://archive.org/details/salamancaavilays00quad/page/${page}/mode/1up`;

/** Elige la hoja que corresponde a una página impresa (la primera hoja de texto con ese número). */
export function pickLeafIndex(pages: FacsimilePage[], printedPage: number | undefined): number {
  if (printedPage === undefined) return 0;
  const exact = pages.findIndex((p) => p.kind === 'text' && p.printedPage === printedPage);
  if (exact >= 0) return exact;
  // número que la paginación impresa salta (p. ej. 647): la página anterior disponible
  let best = 0;
  pages.forEach((p, i) => { if (p.kind === 'text' && p.printedPage !== null && p.printedPage <= printedPage) best = i; });
  return best;
}

/** Devuelve el número de página que la paginación impresa salta entre esta hoja de texto y la siguiente, si lo hay. */
export function missingAfter(pages: FacsimilePage[], i: number): number | null {
  const cur = pages[i];
  if (cur?.kind !== 'text' || cur.printedPage === null) return null;
  const next = pages.slice(i + 1).find((p) => p.kind === 'text' && p.printedPage !== null);
  return next && next.printedPage! > cur.printedPage + 1 ? cur.printedPage + 1 : null;
}

export function Facsimile({ chapterId, printedPage, onPageChange }: { chapterId: string; printedPage: number | undefined; onPageChange: (page: number | undefined) => void }) {
  const [pages, setPages] = useState<FacsimilePage[] | null | undefined>(undefined);
  const [idx, setIdx] = useState(0);
  const [fit, setFit] = useState(true);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([loadFacsimile(), loadBuildInfo().catch(() => null)]).then(([fac, info]) => {
      if (!alive) return;
      if (!fac) { setPages(null); return; }
      const range = info?.chapters.find((c) => c.id === chapterId);
      const list = range ? fac.pages.filter((p) => p.leaf >= range.leafStart && p.leaf <= range.leafEnd) : fac.pages;
      setPages(list);
      setIdx(pickLeafIndex(list, printedPage));
    });
    return () => { alive = false; };
    // la página inicial se toma solo al abrir el visor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  const page = pages?.[idx];
  useEffect(() => { setBroken(false); if (page?.printedPage) onPageChange(page.printedPage); }, [page, onPageChange]);
  const gap = useMemo(() => (pages ? missingAfter(pages, idx) : null), [pages, idx]);

  if (pages === undefined) return <p className="facsimile__status" role="status">Cargando facsímil…</p>;
  if (pages === null || pages.length === 0 || !page) {
    return (
      <div className="facsimile-note">
        <p>El facsímil no está disponible en esta instalación. Se genera con <code>python3 etl/14_build_facsimile.py</code>.</p>
        {printedPage && <p><a href={IA_PAGE(printedPage)} target="_blank" rel="noopener noreferrer">Ver la página {printedPage} en Internet Archive ↗</a></p>}
      </div>
    );
  }

  const go = (d: number) => setIdx((i) => Math.min(pages.length - 1, Math.max(0, i + d)));
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); go(-1); }
    if (e.key === 'ArrowRight' || e.key === 'PageDown') { e.preventDefault(); go(1); }
  };
  const label = page.kind === 'plate' ? `Lámina: ${page.caption ?? 'sin título'}` : `Página ${page.printedPage}`;
  const alt = page.kind === 'plate'
    ? `Lámina de la edición de 1884${page.caption ? `: ${page.caption}` : ''}. Grabado; la descripción textual está en el capítulo.`
    : `Facsímil de la página impresa ${page.printedPage} de la edición de 1884. El texto transcrito está en la vista Texto.`;

  return (
    <section className="facsimile" aria-label="Facsímil de la edición de 1884" onKeyDown={onKey}>
      <div className="facsimile__bar">
        <button type="button" onClick={() => go(-1)} disabled={idx === 0} aria-label="Página anterior">←</button>
        <span className="facsimile__label" aria-live="polite">{label} <span className="muted">· hoja {page.leaf}</span></span>
        <button type="button" onClick={() => go(1)} disabled={idx === pages.length - 1} aria-label="Página siguiente">→</button>
        <button type="button" className="facsimile__zoom" aria-pressed={!fit} onClick={() => setFit((f) => !f)}>{fit ? 'Ampliar' : 'Ajustar'}</button>
      </div>
      {gap && <p className="facsimile__gap" role="note">La numeración impresa salta a la página {gap + 1}: no existe una página {gap} ni falta texto (comprobado en dos ejemplares).</p>}
      <div className={`facsimile__frame${fit ? ' is-fit' : ''}`} tabIndex={0} aria-label={`${label}. Usa las flechas izquierda y derecha para pasar de página.`}>
        {broken ? (
          <p className="facsimile-note">No se pudo cargar la imagen. <a href={IA_PAGE(page.printedPage ?? 0)} target="_blank" rel="noopener noreferrer">Ver en Internet Archive ↗</a></p>
        ) : (
          <img key={page.file} src={facsimileUrl(page.file)} width={page.width} height={page.height} alt={alt}
            decoding="async" onError={() => setBroken(true)} />
        )}
      </div>
      <p className="facsimile__credit">
        Quadrado (1884), dominio público · escaneo de Internet Archive
        {page.printedPage && <> · <a href={IA_PAGE(page.printedPage)} target="_blank" rel="noopener noreferrer">ver en su visor ↗</a></>}
      </p>
    </section>
  );
}
